import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AccountsReceivable, AccountsReceivableCreate, AccountsReceivableUpdate } from '../../../models/AccountsReceivable';
import { AccountsReceivableService, AccountsReceivablePagedResult } from '../../../services/accounts-receivable.service';
import { PaymentService } from '../../../services/payment.service';
import { CustomersService } from '../../../services/customers.service';
import { AsaasCharge, AsaasCustomer, AsaasPaymentResponse } from '../../../models/Payment';
import { Customer } from '../../../models/Customer';
import { ErrorService } from '../../../shared/services/error.service';
import { CashflowService } from '../../../services/cashflow.service';

/** Fluxo exclusivo Asaas (boleto): criar título já vincula cobrança pay_... */
@Component({
  selector: 'app-contract-accounts-receivable-panel',
  templateUrl: './contract-accounts-receivable-panel.component.html',
  styleUrls: ['./contract-accounts-receivable-panel.component.css']
})
export class ContractAccountsReceivablePanelComponent implements OnChanges {
  @Input() contractId!: number;
  @Input() customerId?: number;
  @Input() contractNumber?: string;
  /** Visualização do contrato: sem criar título/boleto manualmente. */
  @Input() readOnly = false;

  titles: AccountsReceivable[] = [];
  /** Cobranças Asaas do cliente vinculadas a este contrato (externalReference CONTRATO-…). */
  asaasCharges: AsaasPaymentResponse[] = [];
  loadingTitles = false;
  titlesError: string | null = null;
  asaasListWarning: string | null = null;

  selectedTitle: AccountsReceivable | null = null;
  /** Cobrança Asaas sem título correspondente no ERP. */
  selectedAsaasOrphan: AsaasPaymentResponse | null = null;
  paymentLoading = false;

  showCreateModal = false;
  createForm = {
    documentNumber: '',
    amount: null as number | null,
    dueDate: '',
    description: ''
  };

  asaasChargeDetails: AsaasPaymentResponse | null = null;
  loadingAsaasCharge = false;
  asaasChargeLoadError: string | null = null;

  private readonly asaasBillingType: 'BOLETO' = 'BOLETO';

  constructor(
    private accountsReceivableService: AccountsReceivableService,
    private paymentService: PaymentService,
    private customersService: CustomersService,
    private errorService: ErrorService,
    private cashflowService: CashflowService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contractId'] && this.contractId) {
      this.loadTitles();
    }
  }

  get saleOrderKey(): string {
    return `CONTRATO-${this.contractId}`;
  }

  loadTitles(): void {
    if (!this.contractId) return;
    this.loadingTitles = true;
    this.titlesError = null;
    this.asaasListWarning = null;

    const byOrder$ = this.accountsReceivableService
      .getBySaleOrderNumber(this.saleOrderKey, { page: 1, pageSize: 200 })
      .pipe(catchError(() => of({ items: [], page: 1, pageSize: 200, totalItems: 0, totalPages: 0 } as AccountsReceivablePagedResult)));

    const byCustomer$ = this.customerId
      ? this.accountsReceivableService
          .getAllAccountsReceivable({ customerId: this.customerId, page: 1, pageSize: 300 })
          .pipe(catchError(() => of({ items: [], page: 1, pageSize: 300, totalItems: 0, totalPages: 0 } as AccountsReceivablePagedResult)))
      : of({ items: [], page: 1, pageSize: 0, totalItems: 0, totalPages: 0 } as AccountsReceivablePagedResult);

    const asaas$ = this.loadAsaasChargesForContract();

    forkJoin({ byOrder: byOrder$, byCustomer: byCustomer$, asaas: asaas$ }).subscribe({
      next: ({ byOrder, byCustomer, asaas }) => {
        this.titles = this.mergeContractTitles(byOrder.items || [], byCustomer.items || []);
        this.asaasCharges = (asaas.charges || []).filter((c) => this.chargeBelongsToContract(c));
        if (asaas.warning) {
          this.asaasListWarning = asaas.warning;
        }
        this.loadingTitles = false;

        const selectId = this.selectedTitleIdBeforeReload ?? this.selectedTitle?.id;
        const selectAsaasId = this.selectedAsaasOrphan?.id;
        this.selectedTitleIdBeforeReload = null;
        if (selectId) {
          const refreshed = this.titles.find((t) => t.id === selectId);
          if (refreshed) {
            this.selectTitle(refreshed);
            return;
          }
        }
        if (selectAsaasId) {
          const refreshedAsaas = this.asaasCharges.find((c) => c.id === selectAsaasId);
          if (refreshedAsaas) {
            this.selectAsaasCharge(refreshedAsaas);
          }
        }
      },
      error: (err) => {
        this.titles = [];
        this.asaasCharges = [];
        this.loadingTitles = false;
        this.titlesError = err?.error?.message || err?.message || 'Erro ao carregar títulos do contrato.';
      }
    });
  }

  /** Une pedido CONTRATO-{id} + títulos do cliente com documento/pedido deste contrato. */
  private mergeContractTitles(
    fromSaleOrder: AccountsReceivable[],
    fromCustomer: AccountsReceivable[]
  ): AccountsReceivable[] {
    const map = new Map<number, AccountsReceivable>();
    [...fromSaleOrder, ...fromCustomer.filter((t) => this.titleBelongsToContract(t))].forEach((t) => {
      if (t?.id != null) {
        map.set(t.id, t);
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.dueDate || '').localeCompare(String(b.dueDate || ''))
    );
  }

  private titleBelongsToContract(ar: AccountsReceivable): boolean {
    const order = (ar.saleOrderNumber || '').trim().toUpperCase();
    if (order === this.saleOrderKey.toUpperCase()) return true;
    const doc = (ar.documentNumber || '').trim().toUpperCase();
    const prefix = `CONTRATO-${this.contractId}`;
    if (doc.startsWith(prefix)) return true;
    const cn = (this.contractNumber || '').trim().toUpperCase();
    if (cn && (ar.contractNumber || '').trim().toUpperCase() === cn) return true;
    if (cn && doc.includes(cn)) return true;
    return false;
  }

  private chargeBelongsToContract(charge: AsaasPaymentResponse): boolean {
    const ref = (charge.externalReference || charge.description || '').trim();
    return this.matchesContractReference(ref);
  }

  private matchesContractReference(ref: string): boolean {
    const r = (ref || '').trim().toUpperCase();
    if (!r) return false;
    const key = this.saleOrderKey.toUpperCase();
    if (r.startsWith(key) || r.includes(key)) return true;
    const prefix = `CONTRATO-${this.contractId}`;
    if (r.startsWith(prefix) || r.includes(prefix)) return true;
    const cn = (this.contractNumber || '').trim().toUpperCase();
    return !!(cn && r.includes(cn));
  }

  private loadAsaasChargesForContract(): Observable<{
    charges: AsaasPaymentResponse[];
    warning?: string;
  }> {
    if (!this.customerId) {
      return of({ charges: [] });
    }
    return this.customersService.getById(this.customerId).pipe(
      switchMap((customer) => {
        const cpf = String(customer.documentNumber || '').replace(/\D/g, '');
        if (!cpf) {
          return of({ charges: [], warning: 'Cliente sem CPF/CNPJ — não foi possível consultar o Asaas.' });
        }
        return this.paymentService.listAsaasCustomers({ cpfCnpj: cpf, limit: 1 }).pipe(
          switchMap((custRes) => {
            const asaasCustomerId = custRes?.data?.[0]?.id as string | undefined;
            if (!asaasCustomerId) {
              return of({ charges: [] });
            }
            return this.fetchAllAsaasChargesForCustomer(asaasCustomerId).pipe(
              map((rows) => ({ charges: rows })),
              catchError(() =>
                of({
                  charges: [],
                  warning: 'Não foi possível listar cobranças no Asaas para este cliente.'
                })
              )
            );
          }),
          catchError(() =>
            of({
              charges: [],
              warning: 'Não foi possível localizar o cliente no Asaas.'
            })
          )
        );
      }),
      catchError(() => of({ charges: [] }))
    );
  }

  private fetchAllAsaasChargesForCustomer(asaasCustomerId: string): Observable<AsaasPaymentResponse[]> {
    const limit = 100;
    const loadPage = (offset: number, acc: AsaasPaymentResponse[]): Observable<AsaasPaymentResponse[]> => {
      return this.paymentService.listAsaasCharges({ customer: asaasCustomerId, limit, offset }).pipe(
        switchMap((res) => {
          const page = this.extractAsaasChargeList(res);
          const merged = [...acc, ...page];
          const total = res?.totalCount ?? res?.TotalCount;
          if (page.length < limit || (total != null && merged.length >= total)) {
            return of(merged);
          }
          return loadPage(offset + limit, merged);
        })
      );
    };
    return loadPage(0, []);
  }

  private extractAsaasChargeList(res: unknown): AsaasPaymentResponse[] {
    if (!res || typeof res !== 'object') return [];
    const r = res as Record<string, unknown>;
    const data = r['data'] ?? r['Data'];
    return Array.isArray(data) ? (data as AsaasPaymentResponse[]) : [];
  }

  openCreateModal(): void {
    const today = new Date().toISOString().split('T')[0];
    const seq = String((this.titles.length || 0) + 1).padStart(2, '0');
    this.createForm = {
      documentNumber: `CTR-${this.contractId}-${seq}`,
      amount: null,
      dueDate: today,
      description: `Título contrato ${this.contractNumber || this.contractId}`
    };
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
  }

  /** Cria o título e em seguida gera boleto Asaas já atrelado (receiptReference = pay_...). */
  submitCreateTitle(): void {
    if (!this.customerId) {
      this.errorService.showErrorMessage('Contrato sem cliente vinculado para criar o título.');
      return;
    }
    const amount = Number(this.createForm.amount) || 0;
    if (amount < 0.01) {
      this.errorService.showErrorMessage('Informe um valor maior que zero.');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const payload: AccountsReceivableCreate = {
      contractNumber: this.contractNumber || String(this.contractId),
      documentNumber: (this.createForm.documentNumber || `CTR-${this.contractId}`).trim(),
      documentType: 'CTR',
      issueDate: today,
      dueDate: this.createForm.dueDate || today,
      originalAmount: amount,
      amountToReceive: amount,
      status: 'Pendente',
      category: 'Venda',
      customerId: this.customerId,
      costCenterId: 1,
      description: this.createForm.description?.trim() || `Contrato #${this.contractNumber || this.contractId}`,
      saleOrderNumber: this.saleOrderKey
    };
    this.paymentLoading = true;
    this.accountsReceivableService.createAccountsReceivable(payload).subscribe({
      next: (created) => {
        this.attachAsaasBoletoToTitle(created, true, true);
      },
      error: (err) => {
        this.paymentLoading = false;
        this.errorService.showErrorMessage(err?.error?.message || 'Erro ao criar título.');
      }
    });
  }

  /** Título antigo sem boleto: gera e vincula no Asaas. */
  generateAsaasBoletoForSelected(): void {
    const title = this.selectedTitle;
    if (!title || !this.canAttachAsaasBoleto(title)) return;
    this.paymentLoading = true;
    this.resolveCustomerForTitle(title, (customer) => {
      this.createAsaasChargeForTitle(title, customer, true, false);
    });
  }

  private attachAsaasBoletoToTitle(
    title: AccountsReceivable,
    openBoleto: boolean,
    fromCreate: boolean
  ): void {
    this.resolveCustomerForTitle(title, (customer) => {
      this.createAsaasChargeForTitle(title, customer, openBoleto, fromCreate);
    });
  }

  private resolveCustomerForTitle(
    title: AccountsReceivable,
    onReady: (customer: Customer) => void
  ): void {
    const customerId = title.customerId || this.customerId;
    if (!customerId) {
      this.paymentLoading = false;
      this.errorService.showErrorMessage('Cliente não informado para gerar boleto Asaas.');
      return;
    }
    this.customersService.getById(customerId).subscribe({
      next: (customer) => {
        if (!customer.documentNumber) {
          this.paymentLoading = false;
          this.errorService.showErrorMessage('Cliente sem CPF/CNPJ — necessário para o Asaas.');
          return;
        }
        onReady(customer);
      },
      error: () => {
        this.paymentLoading = false;
      }
    });
  }

  selectTitle(title: AccountsReceivable): void {
    this.selectedTitle = title;
    this.selectedAsaasOrphan = null;
    this.asaasChargeDetails = null;
    this.asaasChargeLoadError = null;
    this.loadAsaasChargeForTitle(title);
  }

  selectAsaasCharge(charge: AsaasPaymentResponse): void {
    this.selectedAsaasOrphan = charge;
    this.selectedTitle = null;
    this.asaasChargeDetails = this.normalizeAsaasCharge(charge);
    this.asaasChargeLoadError = null;
    this.loadingAsaasCharge = false;
    if (charge.id) {
      this.loadingAsaasCharge = true;
      this.paymentService.getAsaasCharge(charge.id).subscribe({
        next: (raw) => {
          this.asaasChargeDetails = this.normalizeAsaasCharge(raw);
          this.loadingAsaasCharge = false;
        },
        error: (err) => {
          this.loadingAsaasCharge = false;
          this.asaasChargeLoadError =
            err?.error?.message || err?.message || 'Erro ao atualizar cobrança no Asaas.';
        }
      });
    }
  }

  clearSelection(): void {
    this.selectedTitle = null;
    this.selectedAsaasOrphan = null;
    this.asaasChargeDetails = null;
  }

  get asaasOnlyCharges(): AsaasPaymentResponse[] {
    return this.asaasCharges.filter(
      (c) => c.id && !this.titles.some((t) => this.isChargeMatchedToTitle(c, t))
    );
  }

  /** Normaliza nome/referência para comparar ERP × Asaas. */
  private normalizeNameKey(value: string | null | undefined): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .trim()
      .replace(/\s+/g, ' ');
  }

  private namesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
    const ka = this.normalizeNameKey(a);
    const kb = this.normalizeNameKey(b);
    if (!ka || !kb) return false;
    if (ka === kb) return true;
    if (ka.length >= 6 && kb.length >= 6 && (ka.includes(kb) || kb.includes(ka))) {
      return true;
    }
    return false;
  }

  private titleNameKeys(title: AccountsReceivable): string[] {
    const keys: string[] = [];
    const doc = (title.documentNumber || '').trim();
    const desc = (title.description || '').trim();
    if (doc) keys.push(doc);
    if (desc && this.normalizeNameKey(desc) !== this.normalizeNameKey(doc)) {
      keys.push(desc);
    }
    return keys;
  }

  private chargeNameKeys(charge: AsaasPaymentResponse): string[] {
    const keys: string[] = [];
    const ref = (charge.externalReference || '').trim();
    const desc = (charge.description || '').trim();
    if (ref) keys.push(ref);
    if (desc && this.normalizeNameKey(desc) !== this.normalizeNameKey(ref)) {
      keys.push(desc);
    }
    return keys;
  }

  /** Cobrança Asaas correspondente ao título (pay_... ou mesmo nome/referência). */
  findChargeForTitle(title: AccountsReceivable | null): AsaasPaymentResponse | null {
    if (!title) return null;
    const payRef = (title.receiptReference || '').trim();
    if (payRef.toLowerCase().startsWith('pay_')) {
      const byId = this.asaasCharges.find((c) => c.id === payRef);
      if (byId) return byId;
    }
    const titleKeys = this.titleNameKeys(title);
    for (const charge of this.asaasCharges) {
      const chargeKeys = this.chargeNameKeys(charge);
      for (const tk of titleKeys) {
        for (const ck of chargeKeys) {
          if (this.namesMatch(tk, ck)) {
            return charge;
          }
        }
      }
    }
    return null;
  }

  isChargeMatchedToTitle(charge: AsaasPaymentResponse, title: AccountsReceivable): boolean {
    if (charge.id) {
      const payRef = (title.receiptReference || '').trim();
      if (payRef && charge.id === payRef) return true;
    }
    const titleKeys = this.titleNameKeys(title);
    const chargeKeys = this.chargeNameKeys(charge);
    for (const tk of titleKeys) {
      for (const ck of chargeKeys) {
        if (this.namesMatch(tk, ck)) return true;
      }
    }
    return false;
  }

  get hasPanelData(): boolean {
    return this.titles.length > 0 || this.asaasCharges.length > 0;
  }

  getAsaasChargeLabel(charge: AsaasPaymentResponse): string {
    return (charge.externalReference || charge.description || charge.id || 'Cobrança Asaas').trim();
  }

  getAsaasStatusLabel(status?: string | null): string {
    const s = (status || '').toUpperCase();
    const map: Record<string, string> = {
      PENDING: 'Pendente',
      RECEIVED: 'Recebido',
      CONFIRMED: 'Confirmado',
      OVERDUE: 'Vencido',
      CANCELLED: 'Cancelado'
    };
    return map[s] || status || '—';
  }

  getAsaasStatusClass(status?: string | null): string {
    const s = (status || '').toUpperCase();
    if (s === 'RECEIVED' || s === 'CONFIRMED') return 'cv-ar-pill-ok';
    if (s === 'OVERDUE') return 'cv-ar-pill-overdue';
    if (s === 'CANCELLED') return 'cv-ar-pill-muted';
    return 'cv-ar-pill-pending';
  }

  viewAsaasDocumentForCharge(charge: AsaasPaymentResponse): void {
    const url = (charge.bankSlipUrl || charge.invoiceUrl || '').trim();
    if (!url) {
      this.selectAsaasCharge(charge);
      this.viewAsaasDocument();
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  canAttachAsaasBoleto(title: AccountsReceivable | null): boolean {
    if (!title) return false;
    const s = (title.status || '').toLowerCase();
    if (s === 'recebido' || s === 'cancelado') return false;
    return !this.findChargeForTitle(title);
  }

  hasAsaasBoleto(title: AccountsReceivable | null): boolean {
    return !!title && !!this.findChargeForTitle(title);
  }

  resolveAsaasChargeId(title: AccountsReceivable): string | null {
    return this.findChargeForTitle(title)?.id ?? null;
  }

  loadAsaasChargeForTitle(title: AccountsReceivable): void {
    const chargeId = this.resolveAsaasChargeId(title);
    if (!chargeId) {
      this.asaasChargeDetails = null;
      return;
    }
    this.loadingAsaasCharge = true;
    this.asaasChargeLoadError = null;
    this.paymentService.getAsaasCharge(chargeId).subscribe({
      next: (raw) => {
        this.asaasChargeDetails = this.normalizeAsaasCharge(raw);
        this.loadingAsaasCharge = false;
      },
      error: (err) => {
        this.asaasChargeDetails = null;
        this.loadingAsaasCharge = false;
        this.asaasChargeLoadError =
          err?.error?.message || err?.message || 'Erro ao carregar cobrança no Asaas.';
      }
    });
  }

  private normalizeAsaasCharge(dto: AsaasCharge | AsaasPaymentResponse | Record<string, unknown>): AsaasPaymentResponse {
    const d = dto as Record<string, unknown>;
    return {
      id: (d['id'] ?? d['Id']) as string | undefined,
      status: (d['status'] ?? d['Status']) as string | undefined,
      value: Number(d['value'] ?? d['Value']) || undefined,
      billingType: (d['billingType'] ?? d['BillingType']) as string | undefined,
      dueDate: (d['dueDate'] ?? d['DueDate']) as string | undefined,
      invoiceUrl: (d['invoiceUrl'] ?? d['InvoiceUrl']) as string | null | undefined,
      bankSlipUrl: (d['bankSlipUrl'] ?? d['BankSlipUrl']) as string | null | undefined,
      nossoNumero: (d['nossoNumero'] ?? d['NossoNumero']) as string | null | undefined,
      externalReference: (d['externalReference'] ?? d['ExternalReference']) as string | null | undefined
    };
  }

  getAsaasViewUrl(): string | null {
    const url = (this.asaasChargeDetails?.bankSlipUrl || this.asaasChargeDetails?.invoiceUrl || '').trim();
    return url || null;
  }

  viewAsaasDocument(): void {
    let url = this.getAsaasViewUrl();
    if (!url && this.selectedTitle) {
      const matched = this.findChargeForTitle(this.selectedTitle);
      url = (matched?.bankSlipUrl || matched?.invoiceUrl || '').trim();
    }
    if (!url) {
      this.errorService.showErrorMessage('Link do boleto não disponível no Asaas.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private getChargeDueDateForGateway(dueDate: string | Date | undefined): string {
    const today = new Date().toISOString().split('T')[0];
    const accountDue = this.formatDateForApi(dueDate);
    if (!accountDue) return today;
    return accountDue < today ? today : accountDue;
  }

  private getTitleAmount(title: AccountsReceivable): number {
    const remaining = title.remainingAmount;
    if (remaining != null && remaining >= 0) return remaining;
    return Number(title.amountToReceive) || 0;
  }

  private createAsaasChargeForTitle(
    title: AccountsReceivable,
    customer: Customer,
    openBoleto: boolean,
    fromCreate: boolean
  ): void {
    const asaasCustomer = this.buildAsaasCustomer(customer);
    const chargeData: Omit<AsaasCharge, 'customerId'> = {
      billingType: this.asaasBillingType,
      value: this.getTitleAmount(title),
      dueDate: this.getChargeDueDateForGateway(title.dueDate),
      description: title.description || `Contrato ${title.documentNumber}`,
      externalReference: title.documentNumber
    };

    this.paymentService.createAsaasChargeWithCustomerValidation(asaasCustomer, chargeData).subscribe({
      next: (res) => {
        const pay = res as AsaasPaymentResponse;
        const chargeId = pay.id || '';
        const bankSlipNumber = pay.nossoNumero ?? undefined;
        this.updateTitleWithCharge(
          title,
          'Boleto (Asaas)',
          chargeId,
          bankSlipNumber,
          () => {
            this.paymentLoading = false;
            if (fromCreate) {
              this.closeCreateModal();
              this.errorService.showInfoMessage('Título registrado com boleto Asaas vinculado.');
            } else {
              this.errorService.showInfoMessage('Boleto Asaas gerado e vinculado ao título.');
            }
            if (openBoleto && (pay.bankSlipUrl || pay.invoiceUrl)) {
              window.open(pay.bankSlipUrl || pay.invoiceUrl || '', '_blank');
            }
            this.selectedTitleIdBeforeReload = title.id;
            this.cashflowService.requestRefresh();
            this.loadTitles();
          }
        );
      },
      error: () => {
        this.paymentLoading = false;
        if (fromCreate) {
          this.errorService.showErrorMessage(
            'Título criado, mas falhou ao gerar o boleto no Asaas. Selecione o título e use "Gerar boleto Asaas".'
          );
          this.closeCreateModal();
          this.loadTitles();
          this.selectTitleById(title.id);
        }
      }
    });
  }

  private selectedTitleIdBeforeReload: number | null = null;

  private selectTitleById(id: number): void {
    const t = this.titles.find((x) => x.id === id);
    if (t) this.selectTitle(t);
  }

  private buildAsaasCustomer(c: Customer): AsaasCustomer {
    return {
      name: c.name,
      cpfCnpj: String(c.documentNumber).replace(/\D/g, ''),
      email: c.email,
      mobilePhone: c.primaryPhone,
      phone: c.secondaryPhone,
      postalCode: c.zipCode,
      addressNumber: c.number,
      addressComplement: c.complement
    };
  }

  private updateTitleWithCharge(
    title: AccountsReceivable,
    receiptMethod: string,
    receiptReference: string,
    bankSlipNumber: string | undefined,
    onSuccess?: () => void
  ): void {
    const update = this.buildUpdatePayload(title, {
      status: title.status,
      amountReceived: title.amountReceived ?? 0,
      receiptMethod,
      receiptReference,
      bankSlipNumber: bankSlipNumber ?? title.bankSlipNumber
    });
    this.accountsReceivableService.updateAccountsReceivable(update).subscribe({
      next: () => onSuccess?.(),
      error: () => {
        this.paymentLoading = false;
        this.errorService.showErrorMessage('Boleto criado no Asaas, mas falhou ao vincular ao título.');
        this.loadTitles();
      }
    });
  }

  get linkedTitlesCount(): number {
    return this.titles.filter((t) => !!this.findChargeForTitle(t)).length;
  }

  get pendingAsaasCount(): number {
    return this.titles.filter((t) => this.canAttachAsaasBoleto(t)).length;
  }

  get asaasOnlyCount(): number {
    return this.asaasOnlyCharges.length;
  }

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'recebido') return 'cv-ar-pill-ok';
    if (s === 'vencido') return 'cv-ar-pill-overdue';
    if (s === 'cancelado') return 'cv-ar-pill-muted';
    return 'cv-ar-pill-pending';
  }

  getAsaasLinkLabel(title: AccountsReceivable): string {
    if (this.hasAsaasBoleto(title)) return 'Vinculado';
    if (this.canAttachAsaasBoleto(title)) return 'Sem boleto';
    return '—';
  }

  getAsaasLinkClass(title: AccountsReceivable): string {
    if (this.hasAsaasBoleto(title)) return 'cv-ar-pill-asaas-ok';
    if (this.canAttachAsaasBoleto(title)) return 'cv-ar-pill-asaas-missing';
    return 'cv-ar-pill-muted';
  }

  private formatDateForApi(date: Date | string | undefined): string {
    if (!date) return '';
    if (typeof date === 'string') return date.includes('T') ? date.split('T')[0] : date.substring(0, 10);
    return date.toISOString().split('T')[0];
  }

  private buildUpdatePayload(
    ar: AccountsReceivable,
    receipt: {
      status: string;
      amountReceived: number;
      receiptMethod?: string;
      receiptReference?: string;
      receiptDate?: string;
      bankSlipNumber?: string;
    }
  ): AccountsReceivableUpdate {
    const payload: AccountsReceivableUpdate = {
      id: ar.id,
      documentNumber: ar.documentNumber,
      documentType: ar.documentType,
      issueDate: this.formatDateForApi(ar.issueDate),
      dueDate: this.formatDateForApi(ar.dueDate),
      originalAmount: ar.originalAmount,
      discountAmount: ar.discountAmount,
      interestAmount: ar.interestAmount,
      fineAmount: ar.fineAmount,
      amountToReceive: ar.amountToReceive,
      amountReceived: receipt.amountReceived,
      status: receipt.status,
      category: ar.category,
      receiptMethod: receipt.receiptMethod ?? ar.receiptMethod,
      receiptReference: receipt.receiptReference ?? ar.receiptReference,
      customerId: ar.customerId ?? this.customerId,
      costCenterId: ar.costCenterId,
      issuedByStaffId: ar.issuedByStaffId,
      receivedByStaffId: ar.receivedByStaffId,
      description: ar.description,
      invoiceNumber: ar.invoiceNumber,
      saleOrderNumber: ar.saleOrderNumber ?? this.saleOrderKey,
      contractNumber: ar.contractNumber ?? this.contractNumber,
      installmentNumber: ar.installmentNumber,
      totalInstallments: ar.totalInstallments,
      bankSlipNumber: receipt.bankSlipNumber ?? ar.bankSlipNumber,
      checkNumber: ar.checkNumber
    };
    if (receipt.receiptDate?.trim()) {
      payload.receiptDate = receipt.receiptDate.trim();
    }
    return payload;
  }
}
